import {
  ListContextProvider,
  ResourceContextProvider,
  useList,
  useTranslate,
} from "ra-core";

import type { Task } from "../types";
import { TasksIterator } from "./TasksIterator";

/**
 * How many rows a bucket opens with. Desktop used to show five where a phone
 * showed ten, so the screen where triage is actually done showed half of what
 * a pocket did. The desktop budget now leads; the phone keeps the shorter
 * page, because scrolling costs more there.
 */
const ROWS_PER_PAGE = { desktop: 20, mobile: 10 };

/**
 * How many more rows "Load more" reveals. Stepping by ten turned reading a
 * forty-task backlog into four clicks and four scroll jumps.
 */
const LOAD_MORE_STEP = 25;

type TaskListProps = {
  tasks: Task[];
  title: string;
  /**
   * Server-side count for the whole bucket. When set, the heading carries it —
   * "Overdue" alone cannot say whether it holds three tasks or forty, and the
   * fetched rows are only a window onto the bucket.
   */
  total?: number;
  showContact?: boolean;
  showOwner?: boolean;
  /** Set by the due-date list, whose heading already names the day. */
  hideDueDateWhenSingleDay?: boolean;
  includeDone?: boolean;
  /**
   * Translation key rendered under the heading when the bucket is empty. Only
   * the buckets a user acts on daily set it: an Overdue section that vanishes
   * when empty is indistinguishable from one that never existed.
   */
  emptyMessage?: string;
  /** Widens the bucket's own query. Absent once everything is fetched. */
  onLoadMore?: () => void;
  isMobile: boolean;
};

export const TaskListFilter = ({
  tasks,
  title,
  total,
  showContact,
  showOwner,
  hideDueDateWhenSingleDay,
  includeDone,
  emptyMessage,
  onLoadMore,
  isMobile,
}: TaskListProps) => {
  const translate = useTranslate();
  const listContext = useList({
    data: tasks,
    resource: "tasks",
    perPage: isMobile ? ROWS_PER_PAGE.mobile : ROWS_PER_PAGE.desktop,
  });

  const displayTotal = total ?? listContext.total ?? 0;
  const heading =
    total == null
      ? title
      : translate("resources.tasks.filters.with_count", {
          label: title,
          count: total,
        });

  if (!tasks?.length || !listContext.total) {
    if (!emptyMessage) return null;
    return (
      <div className="flex flex-col gap-2">
        <Heading>{heading}</Heading>
        <p className="text-sm text-muted-foreground">
          {translate(emptyMessage)}
        </p>
      </div>
    );
  }

  const handleLoadMore = () => {
    const nextPerPage = listContext.perPage + LOAD_MORE_STEP;
    listContext.setPerPage(nextPerPage);
    // Only reach for more rows once the ones already fetched are all revealed.
    if (nextPerPage >= tasks.length) {
      onLoadMore?.();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Heading>{heading}</Heading>
      <ResourceContextProvider value="tasks">
        <ListContextProvider value={listContext}>
          <TasksIterator
            showContact={showContact}
            showOwner={showOwner}
            hideDueDateWhenSingleDay={hideDueDateWhenSingleDay}
            includeDone={includeDone}
          />
        </ListContextProvider>
      </ResourceContextProvider>
      {displayTotal > listContext.perPage && (
        <div className="flex justify-center">
          <a
            href="#"
            onClick={(e) => {
              handleLoadMore();
              e.preventDefault();
            }}
            className="text-sm underline hover:no-underline"
          >
            {translate("crm.common.load_more")}
          </a>
        </div>
      )}
    </div>
  );
};

/**
 * The bucket heading is the page's only structural anchor, so it is no longer
 * its faintest element: it reads at the weight of a section title, with a
 * hairline rule under it marking where the bucket starts.
 */
const Heading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="border-b pb-1.5 text-sm font-semibold text-foreground">
    {children}
  </h3>
);
